import { Fragment, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, Crown, Medal, Trophy } from 'lucide-react';
import {
  assinaturaClassificacao,
  calcularDeltasPosicao,
  carregarSnapshotPosicoes,
  chaveSnapshotLinha,
  salvarSnapshotPosicoes,
  type DeltaPosicao,
  type LinhaClassificacaoBolao,
  type StatusPremioMatematico,
} from '../../utils/tabelaClassificacaoBolao';

export type { LinhaClassificacaoBolao };

function classeStatusEliminacao(status?: StatusPremioMatematico): string {
  if (status === 'sem_premio') return 'bolao-rank-card--eliminado';
  if (status === 'so_2') return 'bolao-rank-card--so-segundo';
  return '';
}

function rotuloStatusEliminacao(status?: StatusPremioMatematico): string | null {
  if (status === 'sem_premio') return 'ELIMINADO';
  if (status === 'so_2') return 'SÓ 2º';
  return null;
}

function formatarDataHora(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  } catch {
    return iso;
  }
}

function BadgeVariacao({ delta }: { delta: DeltaPosicao }) {
  if (delta.variacao === 'subiu') {
    return (
      <span
        className="bolao-rank-delta bolao-rank-delta--up"
        title={`Subiu ${delta.delta} posição${delta.delta === 1 ? '' : 'ões'}`}
      >
        <ChevronUp className="w-3 h-3 shrink-0" strokeWidth={3} aria-hidden />
        <span className="tabular-nums">{delta.delta}</span>
      </span>
    );
  }
  if (delta.variacao === 'desceu') {
    return (
      <span
        className="bolao-rank-delta bolao-rank-delta--down"
        title={`Desceu ${delta.delta} posição${delta.delta === 1 ? '' : 'ões'}`}
      >
        <ChevronDown className="w-3 h-3 shrink-0" strokeWidth={3} aria-hidden />
        <span className="tabular-nums">{delta.delta}</span>
      </span>
    );
  }
  return null;
}

function estiloPosicao(posicao: number): {
  ring: string;
  podio: string;
  badge: string;
  barra: string;
} {
  if (posicao === 1) {
    return {
      ring: 'bolao-rank-card--ouro',
      podio: 'bolao-podio-card--ouro',
      badge: 'bolao-rank-badge--ouro',
      barra: 'from-amber-400 to-yellow-500',
    };
  }
  if (posicao === 2) {
    return {
      ring: 'bolao-rank-card--prata',
      podio: 'bolao-podio-card--prata',
      badge: 'bolao-rank-badge--prata',
      barra: 'from-neutral-300 to-neutral-500',
    };
  }
  if (posicao === 3) {
    return {
      ring: 'bolao-rank-card--bronze',
      podio: 'bolao-podio-card--bronze',
      badge: 'bolao-rank-badge--bronze',
      barra: 'from-orange-400 to-amber-600',
    };
  }
  return {
    ring: '',
    podio: '',
    badge: 'bolao-rank-badge--default',
    barra: 'from-[#009b3a]/70 to-[#34d399]',
  };
}

function PodioTop3({
  top3,
  deltas,
  destacarId,
  clicavel,
  expandidoId,
  onToggleLinha,
}: {
  top3: LinhaClassificacaoBolao[];
  deltas: Map<string, DeltaPosicao>;
  destacarId?: string;
  clicavel?: boolean;
  expandidoId?: string;
  onToggleLinha?: (id: string) => void;
}) {
  if (top3.length === 0) return null;

  const lugares: Array<{ l?: LinhaClassificacaoBolao; lugar: 1 | 2 | 3; altura: string }> = [
    { l: top3[0], lugar: 1, altura: 'h-[108px] sm:h-[124px]' },
    { l: top3[1], lugar: 2, altura: 'h-[84px] sm:h-[96px]' },
    { l: top3[2], lugar: 3, altura: 'h-[68px] sm:h-[78px]' },
  ];

  return (
    <div className="bolao-podio mb-5">
      <div className="flex items-end justify-center gap-2 sm:gap-3 px-1">
        {lugares.map(({ l, lugar, altura }) => {
          if (!l) {
            return (
              <div
                key={lugar}
                className="flex-1 max-w-[120px] sm:max-w-[148px] opacity-0 pointer-events-none"
                aria-hidden
              />
            );
          }

          const k = chaveSnapshotLinha(l);
          const d = deltas.get(k);
          const ehVoce = destacarId && l.id === destacarId;
          const est = estiloPosicao(lugar);
          const expandido = expandidoId === l.id;
          const statusElim = l.statusEliminacao;
          const rotuloElim = rotuloStatusEliminacao(statusElim);

          return (
            <div
              key={l.id}
              className={`bolao-podio-col flex-1 max-w-[120px] sm:max-w-[148px] flex flex-col items-center ${
                lugar === 1 ? 'bolao-podio-col--lider' : ''
              }`}
            >
              <div
                role={clicavel ? 'button' : undefined}
                tabIndex={clicavel ? 0 : undefined}
                onClick={clicavel ? () => onToggleLinha?.(l.id) : undefined}
                onKeyDown={
                  clicavel
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggleLinha?.(l.id);
                        }
                      }
                    : undefined
                }
                className={`bolao-podio-card w-full rounded-2xl px-2 pt-3 pb-2.5 ${est.podio} ${
                  classeStatusEliminacao(statusElim)
                } ${ehVoce ? 'ring-2 ring-emerald-400' : ''} ${clicavel ? 'bolao-podio-card--click' : ''} ${
                  expandido ? 'ring-2 ring-[#009b3a]/50' : ''
                }`}
              >
                {rotuloElim && (
                  <span
                    className={`bolao-rank-eliminado-label bolao-rank-eliminado-label--podio ${
                      statusElim === 'sem_premio'
                        ? 'bolao-rank-eliminado-label--full'
                        : 'bolao-rank-eliminado-label--so2'
                    }`}
                  >
                    {rotuloElim}
                  </span>
                )}
                <div className="flex items-start justify-between gap-1">
                  <span className={`bolao-rank-badge bolao-rank-badge--sm ${est.badge}`}>
                    {lugar}º
                  </span>
                  {d && <BadgeVariacao delta={d} />}
                </div>
                {lugar === 1 && (
                  <Crown className="w-5 h-5 text-amber-500 mx-auto mt-1 mb-0.5" aria-hidden />
                )}
                {lugar === 2 && (
                  <Medal className="w-4 h-4 text-neutral-500 mx-auto mt-1.5 mb-0.5" aria-hidden />
                )}
                {lugar === 3 && (
                  <Medal className="w-4 h-4 text-orange-600 mx-auto mt-1.5 mb-0.5" aria-hidden />
                )}
                <p className="font-black text-2xl sm:text-3xl text-center text-[#007a2e] tabular-nums leading-none mt-1">
                  {l.pontos}
                </p>
                <p className="text-[9px] font-bold text-neutral-500 text-center uppercase tracking-wider">
                  pontos
                </p>
                <p className="font-bold text-xs sm:text-sm text-center truncate mt-2 px-0.5" title={l.nome}>
                  {l.nome}
                </p>
                {l.setor && (
                  <p className="text-[10px] text-neutral-500 text-center truncate px-0.5" title={l.setor}>
                    {l.setor}
                  </p>
                )}
                {ehVoce && (
                  <span className="mt-1.5 block text-center text-[9px] font-bold uppercase text-emerald-700">
                    Você
                  </span>
                )}
              </div>
              <div className={`bolao-podio-base w-full ${altura} bg-gradient-to-b ${est.barra}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LinhaRanking({
  l,
  delta,
  ehVoce,
  expandido,
  clicavel,
  onToggle,
}: {
  l: LinhaClassificacaoBolao;
  delta: DeltaPosicao;
  ehVoce: boolean;
  expandido: boolean;
  clicavel: boolean;
  onToggle?: () => void;
}) {
  const est = estiloPosicao(l.posicao);
  const temStats =
    l.placarExato != null || l.resultado1x2 != null || (l.mataMata ?? 0) > 0 || l.detalheGrupos != null;
  const rotuloElim = rotuloStatusEliminacao(l.statusEliminacao);
  const eliminado = l.statusEliminacao === 'sem_premio';
  const soSegundo = l.statusEliminacao === 'so_2';

  return (
    <div
      role={clicavel ? 'button' : undefined}
      tabIndex={clicavel ? 0 : undefined}
      onClick={clicavel ? onToggle : undefined}
      onKeyDown={
        clicavel
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle?.();
              }
            }
          : undefined
      }
      className={`bolao-rank-card ${est.ring} ${classeStatusEliminacao(l.statusEliminacao)} ${
        ehVoce ? 'bolao-rank-card--voce' : ''
      } ${clicavel ? 'bolao-rank-card--click' : ''} ${expandido ? 'bolao-rank-card--open' : ''}`}
    >
      {rotuloElim && (
        <span
          className={`bolao-rank-eliminado-label ${
            eliminado ? 'bolao-rank-eliminado-label--full' : 'bolao-rank-eliminado-label--so2'
          }`}
          aria-hidden
        >
          {rotuloElim}
        </span>
      )}
      <div
        className={`flex items-center gap-3 min-w-0 relative z-[1] ${
          eliminado || soSegundo ? 'opacity-40' : ''
        }`}
      >
        <div className="flex flex-col items-center gap-1 shrink-0 w-11">
          <span className={`bolao-rank-badge ${est.badge}`}>{l.posicao}º</span>
          <BadgeVariacao delta={delta} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-bold text-neutral-900 truncate text-sm sm:text-base">{l.nome}</span>
            {ehVoce && (
              <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">
                Você
              </span>
            )}
            {l.pagou && (
              <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full border border-emerald-200">
                Pago
              </span>
            )}
            {(l.faltas ?? 0) > 0 && (
              <span className="text-[9px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                {l.faltas} falta{(l.faltas ?? 0) === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {l.setor && <p className="text-xs text-neutral-500 truncate mt-0.5">{l.setor}</p>}
          {temStats && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {l.detalheGrupos != null && (
                <span className="bolao-stat-chip">Grupos {l.detalheGrupos}</span>
              )}
              {l.placarExato != null && (
                <span className="bolao-stat-chip">{l.placarExato} exatos</span>
              )}
              {l.resultado1x2 != null && (
                <span className="bolao-stat-chip">{l.resultado1x2} venc./emp.</span>
              )}
              {(l.mataMata ?? 0) > 0 && (
                <span className="bolao-stat-chip">MM {l.mataMata}</span>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 text-right pl-1">
          <p className="font-black text-2xl sm:text-3xl text-[#007a2e] tabular-nums leading-none">
            {l.pontos}
          </p>
          <p className="text-[9px] font-semibold text-neutral-500 uppercase tracking-wide">pts</p>
        </div>
      </div>
    </div>
  );
}

export interface TabelaClassificacaoBolaoProps {
  linhas: LinhaClassificacaoBolao[];
  storageKey: string;
  publicadoEm?: string;
  destacarId?: string;
  titulo?: string;
  subtitulo?: string;
  expandidoId?: string;
  onToggleLinha?: (id: string) => void;
  renderExpandido?: (linha: LinhaClassificacaoBolao) => ReactNode;
}

export function TabelaClassificacaoBolao({
  linhas,
  storageKey,
  publicadoEm,
  destacarId,
  titulo = 'Classificação',
  subtitulo,
  expandidoId,
  onToggleLinha,
  renderExpandido,
}: TabelaClassificacaoBolaoProps) {
  const anteriorRef = useRef<Record<string, number>>(carregarSnapshotPosicoes(storageKey));
  const assinatura = useMemo(() => assinaturaClassificacao(linhas), [linhas]);

  const deltas = useMemo(
    () => calcularDeltasPosicao(linhas, anteriorRef.current),
    [linhas, assinatura]
  );

  useEffect(() => {
    salvarSnapshotPosicoes(storageKey, linhas);
    anteriorRef.current = carregarSnapshotPosicoes(storageKey);
  }, [assinatura, linhas, storageKey]);

  const top3 = linhas.slice(0, 3);
  const restante = linhas.slice(3);
  const clicavel = Boolean(onToggleLinha);

  if (linhas.length === 0) {
    return (
      <p className="text-center text-neutral-600 py-6 text-sm">Nenhum participante na classificação.</p>
    );
  }

  return (
    <div className="bolao-classificacao space-y-4">
      {(titulo || publicadoEm) && (
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            {titulo && (
              <h3 className="text-lg font-black text-neutral-900 flex items-center gap-2 tracking-tight">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-md shadow-amber-200/60">
                  <Trophy className="w-5 h-5 text-white" />
                </span>
                {titulo}
              </h3>
            )}
            {subtitulo && <p className="text-xs text-neutral-600 mt-1 pl-11">{subtitulo}</p>}
          </div>
          {publicadoEm && (
            <p className="text-[11px] text-emerald-900 bg-white/80 backdrop-blur border border-emerald-200/80 rounded-full px-3 py-1 shadow-sm">
              Atualizado {formatarDataHora(publicadoEm)}
            </p>
          )}
        </div>
      )}

      <PodioTop3
        top3={top3}
        deltas={deltas}
        destacarId={destacarId}
        clicavel={clicavel}
        expandidoId={expandidoId}
        onToggleLinha={onToggleLinha}
      />

      {expandidoId && top3.some((l) => l.id === expandidoId) && renderExpandido && (
        <div className="bolao-rank-expand px-3 py-3 -mt-2 rounded-2xl border border-neutral-200/80 bg-white/95 shadow-sm">
          {renderExpandido(top3.find((l) => l.id === expandidoId)!)}
        </div>
      )}

      <div className="space-y-2">
        {restante.map((l) => {
          const k = chaveSnapshotLinha(l);
          const d = deltas.get(k) ?? { variacao: 'igual' as const, delta: 0 };
          const ehVoce = Boolean(destacarId && l.id === destacarId);
          const expandido = expandidoId === l.id;

          return (
            <Fragment key={l.id}>
              <LinhaRanking
                l={l}
                delta={d}
                ehVoce={ehVoce}
                expandido={expandido}
                clicavel={clicavel}
                onToggle={clicavel ? () => onToggleLinha?.(l.id) : undefined}
              />
              {expandido && renderExpandido && (
                <div className="bolao-rank-expand px-3 py-3 -mt-1 mb-1 rounded-b-2xl border border-t-0 border-neutral-200/80 bg-white/90">
                  {renderExpandido(l)}
                </div>
              )}
              {expandido &&
                !renderExpandido &&
                (l.placarExato != null || l.detalheGrupos != null) && (
                  <div className="bolao-rank-expand px-3 py-2 -mt-1 mb-1 rounded-b-2xl border border-t-0 border-neutral-200/80 bg-white/90 text-xs text-neutral-700">
                    <ul className="space-y-0.5">
                      {l.detalheGrupos != null && <li>Grupos: {l.detalheGrupos} pts</li>}
                      {l.placarExato != null && (
                        <li>
                          {l.placarExato} placares exatos · {l.resultado1x2 ?? 0} vencedor/empate
                        </li>
                      )}
                      {(l.mataMata ?? 0) > 0 && (
                        <li>
                          Mata-mata: {l.mataMata} pts ({l.placarExatoMM ?? 0} exatos,{' '}
                          {l.acertoVencedorMM ?? 0} vencedor)
                        </li>
                      )}
                    </ul>
                  </div>
                )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
